import {IconButton} from '@wandb/components';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
// @todo: Migrate to 'TagGroup' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tag} from '@wandb/weave/components/Tag';
// @todo: Migrate to 'Tooltip' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {clsx} from 'clsx';
import React, {useMemo, useState} from 'react';
import {useParams} from 'react-router';

import {trackObservabilityExpandGroupedIssue} from '../../../services/analytics/observabilityEvents';
import {useIssuesContext} from '../../../state/issues/IssuesContext';
import {Issue as IssueType} from '../../../state/issues/types';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';

export const RunIssuesList = ({
  hoveredIssue,
  issues,
}: {
  hoveredIssue: string | null;

  issues: IssueType[];
}) => {
  const {isSingleRun} = useSingleRunModeContext();
  return (
    <section data-test="run-issues-list">
      <ul>
        {issues
          .sort((a, b) => b.timestamp - a.timestamp)
          .map(issue => (
            <Issue
              isActive={hoveredIssue === issue?.id && isSingleRun}
              key={issue?.id}
              {...issue}
            />
          ))}
      </ul>
    </section>
  );
};

export const GroupedRunIssuesList = ({
  hoveredIssue,
  groupedIssues,
}: {
  hoveredIssue: string | null;
  groupedIssues: IssueType[][];
}) => {
  const {isSingleRun} = useSingleRunModeContext();
  return (
    <section data-test="run-issues-list">
      {(groupedIssues ?? []).map((gIssues, index) =>
        gIssues.length > 1 ? (
          <IssueGroup
            hoveredIssue={isSingleRun ? hoveredIssue : null}
            key={index}
            issues={gIssues}
          />
        ) : (
          <Issue
            isActive={hoveredIssue === gIssues[0].id}
            key={gIssues[0].id}
            {...gIssues[0]}
          />
        )
      )}
    </section>
  );
};

export const IssueGroup = ({
  issues,
  hoveredIssue,
}: {
  issues: IssueType[];
  hoveredIssue: string | null;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();

  const isActive = useMemo(() => {
    return isHeaderHovered || issues.some(issue => hoveredIssue === issue.id);
  }, [isHeaderHovered, hoveredIssue, issues]);

  const classes = clsx({
    'border-teal-600': isActive,
    'border-moon-250': !isActive,
  });

  const i = useMemo(
    () => (isExpanded ? issues : issues.slice(0, 1)),
    [issues, isExpanded]
  );
  return (
    <div className={`${classes} mb-16 rounded-[4px] border-[1px] border-solid`}>
      <div
        className={` flex items-center justify-between px-16 py-8`}
        onMouseEnter={() => {
          setIsHeaderHovered(true);
        }}
        onMouseLeave={() => {
          setIsHeaderHovered(false);
        }}>
        <div className="flex items-center gap-4 text-base">
          {issues.length} separate but similar issues{' '}
          <Tooltip
            content="Similar issues are often due to the same issue happening concurrently on multiple nodes."
            trigger={<Icon name="info" height={16} />}
          />
        </div>
        <IconButton
          aria-label={isExpanded ? 'collapse' : 'expand'}
          icon={isExpanded ? 'collapse' : 'expand-uncollapse'}
          onClick={() => {
            if (!isExpanded) {
              trackObservabilityExpandGroupedIssue({
                action: 'Expand grouped issue',
                alertName: issues[0]?.alertName ?? 'Unknown',
                alertsInGroup: issues.length,
                entityName: entityName ?? '',
                projectName: projectName ?? '',
              });
            }
            setIsExpanded(!isExpanded);
          }}
          variant="secondary"
        />
      </div>

      <div>
        {(i ?? []).map(issue => (
          <Issue
            key={issue.id}
            {...issue}
            isActive={hoveredIssue === issue.id}
            grouped
          />
        ))}
      </div>
    </div>
  );
};

export const Issue = ({
  description,
  id,
  isActive,
  grafanaDashboard,
  grouped,
  labels,
  node,
  timestamp,
  title,
}: IssueType & {
  isActive: boolean;
  grouped?: boolean;
}) => {
  const classes = clsx({
    'border-teal-600': isActive && !grouped,
    'border-moon-250': !isActive || grouped,
    'border-t-[1px]': grouped,
    'border-[1px] rounded-lg': !grouped,
    'mt-16': !grouped,
  });
  const {handleIssueInteraction} = useIssuesContext();

  return (
    <article
      className={` px-16 py-20 ${classes}`}
      data-test="run-issue-card"
      id={id}
      onMouseEnter={handleIssueInteraction('hover', id)}
      onMouseLeave={handleIssueInteraction('unhover', null)}>
      <div className="text-lg font-semibold">{title}</div>
      <div className="my-8 flex items-center justify-start gap-10">
        <Tag
          color="moon"
          label={
            labels?.component && labels.component === 'application'
              ? 'Application'
              : 'Infrastructure'
          }
        />
        <div className="h-4 w-4 rounded-full bg-moon-650" />
        <div className="text-moon-650">
          {new Date(timestamp).toLocaleString()}
        </div>
        <div className="h-4 w-4 rounded-full bg-moon-650" />
        <Button
          variant="ghost"
          onClick={() => {
            // eslint-disable-next-line
            window.open(grafanaDashboard, '_blank', 'noopener noreferrer');
          }}>
          <span className="flex items-center gap-4">
            <Icon name="grafana" />
            Grafana
          </span>
        </Button>
      </div>
      <p className="mb-12 text-sm">{description}</p>

      <p>
        <span className="pr-4 text-[15px] font-semibold">Node:</span> {node}
      </p>
    </article>
  );
};
